import React from 'react';
import {
  BG_CARD,
  BORDER_LIGHT,
  TEXT_DARK,
  TEXT_MUTED,
  PRIMARY,
} from '../components/legacy-ball/table-management/designTokens';
import { useOperationsRoleEdit } from '../hooks/useOperationsRoleEdit';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface OperationsRoleDto {
  id: string;
  roleName: string;
  assigneeName: string | null;
  phone: string | null;
  status: string | null;
}

export interface OperationsRolesPanelProps {
  eventId: string;
  operationsRoles: OperationsRoleDto[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onRoleUpdated: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const OPERATIONS_ROLES = [
  'MC',
  'DJ',
  'Audio',
  'Video',
  'Facilities',
  'Medical',
  'Grounds & Parking',
] as const;

export type OperationsRoleName = (typeof OPERATIONS_ROLES)[number];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
  },
  heading: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 1rem 0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem 0.75rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  td: {
    padding: '0.5rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    verticalAlign: 'middle' as const,
  },
  tdMuted: {
    padding: '0.5rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_MUTED,
    verticalAlign: 'middle' as const,
  },
  editBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s',
  },
  editBtnDisabled: {
    background: 'none',
    border: 'none',
    cursor: 'not-allowed',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.3,
  },
  actionBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s',
  },
  input: {
    width: '100%',
    padding: '4px 6px',
    fontSize: '0.8rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  errorRow: {
    color: '#d32f2f',
    fontSize: '0.8rem',
    padding: '0.25rem 0.75rem 0.5rem',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  errorContainer: {
    textAlign: 'center' as const,
    padding: '2rem 1rem',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.85rem',
    marginBottom: '0.75rem',
  },
  retryBtn: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
    minWidth: '44px',
    minHeight: '44px',
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function OperationsRolesPanel({
  eventId,
  operationsRoles,
  loading,
  error,
  onRetry,
  onRoleUpdated,
}: OperationsRolesPanelProps) {
  const {
    editingRole,
    editValues,
    saving,
    saveError,
    startEdit,
    cancelEdit,
    updateField,
    saveEdit,
  } = useOperationsRoleEdit(eventId, onRoleUpdated);

  // Build a lookup map from fetched roles
  const roleMap = new Map<string, OperationsRoleDto>();
  for (const role of operationsRoles) {
    roleMap.set(role.roleName, role);
  }

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <h3 style={styles.heading}>Operations Roles &amp; Responsibilities</h3>
        <div style={styles.loadingContainer}>
          <span aria-label="Loading operations roles">⏳ Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <h3 style={styles.heading}>Operations Roles &amp; Responsibilities</h3>
        <div style={styles.errorContainer}>
          <div style={styles.errorText}>
            ⚠️ Unable to load operations role data.
          </div>
          {onRetry && (
            <button
              style={styles.retryBtn}
              onClick={onRetry}
              aria-label="Retry loading operations roles"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Operations Roles &amp; Responsibilities</h3>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Role</th>
            <th style={styles.th}>Assigned To</th>
            <th style={styles.th}>Phone</th>
            <th style={styles.th}>Status</th>
            <th style={{ ...styles.th, textAlign: 'center' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {OPERATIONS_ROLES.map((roleName) => {
            const roleData = roleMap.get(roleName) ?? null;
            const isAssigned = roleData && roleData.assigneeName;
            const isEditing = editingRole === roleName;

            return (
              <React.Fragment key={roleName}>
                <tr>
                  <td style={styles.td}>{roleName}</td>

                  {/* Assigned To */}
                  <td style={isEditing ? styles.td : isAssigned ? styles.td : styles.tdMuted}>
                    {isEditing ? (
                      <input
                        style={styles.input}
                        type="text"
                        value={editValues.assigneeName}
                        onChange={(e) => updateField('assigneeName', e.target.value)}
                        maxLength={100}
                        placeholder="Assignee name"
                        aria-label={`Assignee name for ${roleName}`}
                      />
                    ) : (
                      isAssigned ? roleData.assigneeName : 'Unassigned'
                    )}
                  </td>

                  {/* Phone */}
                  <td style={isEditing ? styles.td : isAssigned ? styles.td : styles.tdMuted}>
                    {isEditing ? (
                      <input
                        style={styles.input}
                        type="text"
                        value={editValues.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        maxLength={20}
                        placeholder="Phone"
                        aria-label={`Phone for ${roleName}`}
                      />
                    ) : (
                      roleData?.phone || '-'
                    )}
                  </td>

                  {/* Status */}
                  <td style={isEditing ? styles.td : isAssigned ? styles.td : styles.tdMuted}>
                    {isEditing ? (
                      <input
                        style={styles.input}
                        type="text"
                        value={editValues.status}
                        onChange={(e) => updateField('status', e.target.value)}
                        maxLength={50}
                        placeholder="Status"
                        aria-label={`Status for ${roleName}`}
                      />
                    ) : (
                      roleData?.status || '-'
                    )}
                  </td>

                  {/* Action */}
                  <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {isEditing ? (
                      <>
                        <button
                          style={styles.actionBtn}
                          onClick={saveEdit}
                          disabled={saving}
                          aria-label={`Save ${roleName} assignment`}
                          title="Save"
                        >
                          ✓
                        </button>
                        <button
                          style={styles.actionBtn}
                          onClick={cancelEdit}
                          disabled={saving}
                          aria-label={`Cancel editing ${roleName}`}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <button
                        style={editingRole !== null ? styles.editBtnDisabled : styles.editBtn}
                        onClick={() => startEdit(roleName, roleData)}
                        disabled={editingRole !== null}
                        aria-label={`Edit ${roleName} assignment`}
                        title={`Edit ${roleName} assignment`}
                      >
                        ✏️
                      </button>
                    )}
                  </td>
                </tr>

                {/* Save error message below the editing row */}
                {isEditing && saveError && (
                  <tr>
                    <td colSpan={5} style={styles.errorRow}>
                      ⚠️ {saveError}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
